import { useState, useEffect, useCallback, useRef } from 'react';
import axios from '../utils/axios';
import { PerformanceHistoryItem } from './useStatistics';

export interface TraderOverviewRow {
  trader_id: number; name: string; strategy: string;
  net_liquid_assets: number | null; deposits: number | null;
  realized_pnl: number; unrealized_pnl: number;
  win_rate: number; total_trades: number; open_positions: number;
}
export interface OverviewTotals {
  net_liquid_assets: number; realized_pnl: number; unrealized_pnl: number; open_positions: number;
}
export interface OverviewResponse { traders: TraderOverviewRow[]; totals: OverviewTotals; }

// Equity-curve points come straight from get_performance_history, the same source
// useStatistics types as PerformanceHistoryItem — reuse it instead of redefining.
export interface EquityCurve { trader_id: number; name: string; data: PerformanceHistoryItem[]; }

export function useGet<T>(url: string) {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Monotonic request counter: a response is applied only if it's still the
  // latest in-flight request. Guards against out-of-order overwrites (url change)
  // and setState after unmount, matching TransactionHistory's fetchSeq precedent.
  const fetchSeq = useRef(0);
  const fetchData = useCallback(async () => {
    const seq = ++fetchSeq.current;
    try {
      setLoading(true);
      const response = await axios.get(url);
      if (seq !== fetchSeq.current) return;  // stale response; a newer request is in flight
      setData(response.data);
      setError(null);
    } catch (e) {
      if (seq !== fetchSeq.current) return;  // stale response; a newer request is in flight
      console.error(`Error fetching ${url}:`, e);
      setError('Failed to load data');
    } finally {
      if (seq === fetchSeq.current) setLoading(false);
    }
  }, [url]);
  useEffect(() => {
    fetchData();
    // On unmount (or url change), bump the counter so any in-flight response is
    // treated as stale and skips its setState.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: bumping fetchSeq in cleanup stale-marks in-flight responses
    return () => { fetchSeq.current++; };
  }, [fetchData]);
  return { data, loading, error, refetch: fetchData };
}

const withDates = (base: string, startDate?: string, endDate?: string): string => {
  const qs = new URLSearchParams();
  if (startDate) qs.set('start_date', startDate);
  if (endDate) qs.set('end_date', endDate);
  const query = qs.toString();
  return query ? `${base}?${query}` : base;
};

export const useOverview = (startDate?: string, endDate?: string) =>
  useGet<OverviewResponse>(withDates('/statistics/overview', startDate, endDate));
export const useEquityCurves = (startDate?: string, endDate?: string) =>
  useGet<{ curves: EquityCurve[] }>(withDates('/statistics/equity-curves', startDate, endDate));
